import React from "react";
import { ModalCompAdvance } from "../../../../components";
import {
  formatMoney,
  convertToTitleCase,
  NAIRA_SIGN,
  getTransactionSignedAmountForDisplay,
} from "../../../../utils";
import moment from "moment";
import { AiOutlineClose } from "react-icons/ai";
import { useGetMemberTransactionByIdQuery } from "../../../../store/member/member-transaction-history";

const MemberTransactionDetailsModal = ({ transactionId, setModalPage }) => {
  const {
    data: transactionData,
    isLoading,
    isError,
  } = useGetMemberTransactionByIdQuery(transactionId, {
    skip: !transactionId,
  });

  if (isLoading) {
    return (
      <ModalCompAdvance
        show={true}
        handleClose={() => setModalPage("")}
        size="lg"
        centered
        close={true}
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="p-4 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading transaction details...</p>
              </div>
            ),
          },
        ]}
      />
    );
  }

  // Check for errors or missing transaction data
  // Handle case where transformResponse might not have worked (defensive check)
  let transaction = transactionData;
  if (Array.isArray(transactionData) && transactionData.length > 0) {
    transaction = transactionData[0];
  }

  if (isError || !transaction || !transaction.id) {
    return (
      <ModalCompAdvance
        show={true}
        handleClose={() => setModalPage("")}
        size="lg"
        centered
        close={true}
        modalTitle={
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="fw-bold mb-0">Transaction Details</h5>
            <AiOutlineClose
              onClick={() => setModalPage("")}
              className="cursor-pointer"
              size={24}
              style={{ color: "var(--color-primary)" }}
            />
          </div>
        }
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="text-center py-5">
                <p className="text-danger">Unable to load transaction details.</p>
                {isError && (
                  <p className="text-muted small mt-2">
                    Please try again later or contact support if the issue persists.
                  </p>
                )}
              </div>
            ),
          },
        ]}
      />
    );
  }

  // Get status badge color
  const getStatusColor = (status) => {
    const statusUpper = (status || "").toUpperCase();
    if (statusUpper === "SUCCESSFUL" || statusUpper === "PROCESSED" || statusUpper === "PAID") {
      return "#19BC31"; // Green
    }
    if (statusUpper === "PENDING") {
      return "#FFA500"; // Orange
    }
    if (statusUpper === "FAILED" || statusUpper === "DECLINED") {
      return "#FF4343"; // Red
    }
    return "#A1A0A0"; // Grey
  };

  // Get transaction type display name
  const getTransactionTypeName = (transaction) => {
    return transaction?.display_type || convertToTitleCase(transaction?.type) || "Transaction";
  };

  // Get payment method display name
  const getPaymentMethodName = (transaction) => {
    return transaction?.display_payment_method || transaction?.payment_method || "N/A";
  };

  const parseJsonMaybe = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    if (typeof value !== "string") return null;
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  };

  const paymentData = parseJsonMaybe(
    transaction?.payment_data ||
      transaction?.model?.payment_data
  );

  const companyBank =
    transaction?.company_bank ||
    transaction?.companyBank ||
    transaction?.model?.company_bank ||
    transaction?.model?.companyBank ||
    paymentData?.company_bank ||
    paymentData?.companyBank ||
    null;

  const memberNote =
    transaction?.member_note ||
    transaction?.model?.member_note ||
    paymentData?.member_note ||
    "";

  const receiptSources = [
    transaction?.receipt_url,
    transaction?.receipt_path,
    transaction?.model?.receipt_url,
    transaction?.model?.receipt_path,
    paymentData?.receipt_url,
    paymentData?.receipt_path,
  ].filter(Boolean);

  const receiptPaths = [
    ...(Array.isArray(transaction?.receipt_paths) ? transaction.receipt_paths : []),
    ...(Array.isArray(transaction?.model?.receipt_paths)
      ? transaction.model.receipt_paths
      : []),
    ...(Array.isArray(paymentData?.receipt_paths) ? paymentData.receipt_paths : []),
  ].filter(Boolean);

  const receiptLinks = [...new Set([...receiptSources, ...receiptPaths])];

  const additionalInfoItems = [
    transaction?.model?.contribution_category?.name
      ? {
          label: "Category",
          value: transaction.model.contribution_category.name,
        }
      : null,
    transaction?.model?.loan_type?.name
      ? {
          label: "Loan Type",
          value: transaction.model.loan_type.name,
        }
      : null,
    companyBank?.bank_name
      ? {
          label: "Transferred To Bank",
          value: companyBank.bank_name,
        }
      : null,
    companyBank?.account_name
      ? {
          label: "Account Name",
          value: companyBank.account_name,
        }
      : null,
    companyBank?.account_number
      ? {
          label: "Account Number",
          value: companyBank.account_number,
        }
      : null,
    memberNote
      ? {
          label: "Member Note",
          value: memberNote,
        }
      : null,
  ].filter(Boolean);

  // transaction is already defined above from the error check
  const statusColor = getStatusColor(transaction.status);
  const transactionType = getTransactionTypeName(transaction);

  const safeAmount = getTransactionSignedAmountForDisplay(transaction);
  const amountColor =
    safeAmount < 0 ? "#d40000" : safeAmount > 0 ? "#19BC31" : "var(--color-primary)";
  const flowLabel =
    safeAmount < 0 ? "Money out (debit)" : safeAmount > 0 ? "Money in (credit)" : null;

  return (
    <ModalCompAdvance
      show={true}
      handleClose={() => setModalPage("")}
      size="lg"
      centered
      close={true}
      modalTitle={
        <div className="d-flex justify-content-between align-items-center w-100">
          <h4 className="fw-bold mb-0" style={{ color: "var(--color-primary)" }}>
            Transaction Details
          </h4>
          <AiOutlineClose
            onClick={() => setModalPage("")}
            className="cursor-pointer"
            size={24}
            style={{ color: "var(--color-primary)" }}
          />
        </div>
      }
      modalBody={0}
      arrayComp={[
        {
          component: (
            <div className="p-4">
              <div className="w-100 d-flex flex-column" style={{ gap: "16px" }}>
                {/* Transaction Type */}
                <div className="w-100 pb-3 border-bottom">
                  <p className="small text-muted mb-1">Transaction Type</p>
                  <p className="small fw-bold text-black1 mb-0">
                    {transactionType}
                  </p>
                </div>

                {/* Amount */}
                <div className="w-100 pb-3 border-bottom">
                  <p className="small text-muted mb-1">Amount</p>
                  <p className="fs-5 fw-bold mb-0" style={{ color: amountColor }}>
                    {formatMoney(safeAmount.toFixed(2), true, NAIRA_SIGN)}
                  </p>
                  {flowLabel ? (
                    <p className="small text-muted mb-0 mt-1">{flowLabel}</p>
                  ) : null}
                </div>

                {/* Reference */}
                <div className="w-100 pb-3 border-bottom">
                  <p className="small text-muted mb-1">Transaction Reference</p>
                  <p className="small fw-bold text-black1 mb-0" style={{ fontFamily: "monospace" }}>
                    {transaction?.reference || "N/A"}
                  </p>
                </div>

                {/* Payment Method */}
                <div className="w-100 pb-3 border-bottom">
                  <p className="small text-muted mb-1">Payment Method</p>
                  <p className="small fw-bold text-black1 mb-0">
                    {getPaymentMethodName(transaction)}
                  </p>
                </div>

                {/* Status */}
                <div className="w-100 pb-3 border-bottom">
                  <p className="small text-muted mb-1">Status</p>
                  <span
                    className="rounded-pill px-3 py-1 text-white"
                    style={{
                      backgroundColor: statusColor,
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {convertToTitleCase(transaction?.status || "UNKNOWN")}
                  </span>
                </div>

                {/* Date & Time */}
                {(transaction?.created_at || transaction?.date) && (
                  <div className="w-100 pb-3 border-bottom">
                    <p className="small text-muted mb-1">Transaction Date</p>
                    <div>
                      <p className="small fw-bold text-black1 mb-0">
                        {moment(transaction?.created_at || transaction?.date).format("DD MMMM, YYYY")}
                      </p>
                      <p className="text-muted mb-0" style={{ fontSize: "11px" }}>
                        {moment(transaction?.created_at || transaction?.date).format("h:mm A")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Remark/Description - Only show if it's meaningful (not just a number or single character) */}
                {transaction?.remark && 
                 transaction.remark.trim().length > 1 && 
                 isNaN(transaction.remark.trim()) && (
                  <div className="w-100 pb-3 border-bottom">
                    <p className="small text-muted mb-1">Description</p>
                    <p className="small fw-semibold text-black1 mb-0">
                      {transaction.remark}
                    </p>
                  </div>
                )}

                {/* Additional Details from Model / bank transfer metadata */}
                {(transaction?.model || additionalInfoItems.length > 0 || receiptLinks.length > 0) && (
                  <div className="w-100 pb-3 border-bottom">
                    <p className="small text-muted mb-1">Additional Information</p>
                    <div className="small text-black1">
                      {additionalInfoItems.map((item) => (
                        <p className="mb-1" key={item.label}>
                          <strong>{item.label}:</strong> {item.value}
                        </p>
                      ))}
                      {receiptLinks.map((receipt, index) => (
                        <p className="mb-1" key={`${receipt}-${index}`}>
                          <strong>{receiptLinks.length > 1 ? `Receipt ${index + 1}` : "Receipt"}:</strong>{" "}
                          {String(receipt).startsWith("http") ? (
                            <a
                              href={receipt}
                              target="_blank"
                              rel="noreferrer"
                              className="text-decoration-underline"
                            >
                              View uploaded receipt
                            </a>
                          ) : (
                            <span>{receipt}</span>
                          )}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </div>
          ),
        },
      ]}
    />
  );
};

export default MemberTransactionDetailsModal;

